import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/Tabs';
import { EditableField } from '../ui/EditableField';
import { TagInput } from '../ui/TagInput';
import FileUpload from '../ui/FileUpload';
import ProfessionalPDFExtractionButton from '../pdf/ProfessionalPDFExtractionButton';
import { useFinancialInstitution } from '../../hooks/useFinancialInstitution';
import { useUser } from '../../hooks/useUser';
import { useFormSecurity } from '../../hooks/useFormSecurity';
import { useToast } from '../../hooks/useToast';
import { useBackendHealth } from '../../hooks/useBackendHealth';
import { EntityLogo } from './EntityLogo';
import { Edit3, Save, X, Loader2, Building2, Shield, FileText, Handshake, AlertCircle, WifiOff, RefreshCw } from 'lucide-react';
import {
  FINANCIAL_INSTITUTION_TYPES,
  FINANCIAL_INSTITUTION_SUBTYPES,
  SUPERVISORY_AUTHORITIES,
  CLIENT_SEGMENTS,
  LEGAL_STATUSES,
  CURRENCIES,
} from '../../constants/financialInstitutionOptions';
import { getAfricanCountryOptions } from '../../constants/countries';
import financialInstitutionApiService from '../../services/financialInstitutionApi';
import {
  formDraftKey,
  saveFormDraft,
  loadFormDraft,
  clearFormDraft,
} from '../../utils/formDraft';
import { InstitutionBranchesSection } from './InstitutionBranchesSection';

/**
 * ✨ Helper pour convertir les dates ISO en format YYYY-MM-DD pour les inputs HTML date
 * Le backend retourne: "2025-12-01T00:00:00.000Z"
 * Les inputs date attendent: "2025-12-01"
 */
const formatDateForInput = (dateValue: string | null | undefined): string => {
  if (!dateValue) return '';
  
  try {
    // Si c'est déjà au format YYYY-MM-DD, le retourner tel quel
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    
    // Extraire la partie date d'une chaîne ISO 8601
    // Format: "2025-12-01T00:00:00.000Z" → "2025-12-01"
    const isoMatch = dateValue.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) {
      return isoMatch[1];
    }
    
    // Essayer de parser comme Date et formater
    const date = new Date(dateValue);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
    
    return '';
  } catch (error) {
    console.warn('⚠️ Erreur de formatage de date:', dateValue, error);
    return '';
  }
};

interface FinancialInstitutionFormProps {
  financialInstitutionId?: string;
}

const FinancialInstitutionForm: React.FC<FinancialInstitutionFormProps> = ({ financialInstitutionId }) => {
  // ✨ Récupérer l'ID de l'institution depuis le profil utilisateur (comme pour Company)
  const { user, isLoading: userLoading } = useUser();
  
  // ✨ NOUVEAU: Vérifier la santé du backend
  // ✨ IMPORTANT: status permet de savoir si la vérification a été faite (unknown = pas encore vérifié)
  const { isHealthy: isBackendHealthy, isChecking: isCheckingBackend, checkHealth, status: backendStatus } = useBackendHealth();
  
  // ✨ IMPORTANT: Utiliser l'ID des props OU celui du profil utilisateur
  // Mémoriser pour éviter les re-calculs inutiles
  const actualInstitutionId = React.useMemo(() => {
    return financialInstitutionId || user?.financialInstitutionId;
  }, [financialInstitutionId, user?.financialInstitutionId]);
  
  // 🔍 Debug: Log à chaque render pour voir les valeurs (avec protection contre les logs excessifs)
  React.useEffect(() => {
    console.log('🔄 FinancialInstitutionForm RENDER:', { 
      userLoaded: !!user,
      userLoading,
      userFinancialInstitutionId: user?.financialInstitutionId,
      actualInstitutionId,
      isBackendHealthy
    });
  }, [user?.id, user?.financialInstitutionId, userLoading, actualInstitutionId, isBackendHealthy]);
  
  // ✨ Utiliser le hook avec l'ID réel (peut être undefined pour création)
  // hasValidUUID indique si l'ID est un UUID valide (pas un ID mock)
  const { 
    institution, 
    updateInstitutionData, 
    loading, 
    hasValidUUID,
    uploadLogoFile,
    isUpdating: isLogoUploading
  } = useFinancialInstitution(actualInstitutionId);
  
  // ✨ ANTI-VIBRATION: Ref pour tracker l'ID de l'institution déjà initialisée
  const initializedInstitutionIdRef = React.useRef<string | null>(null);

  // ── Persistance du BROUILLON (anti-perte de données) ────────────────────
  // Cf. EnterpriseIdentificationForm : sauvegarde les saisies non encore
  // « Sauvegardées » côté backend pour survivre à un rafraîchissement de page
  // ou au démontage (changement d'onglet / navigation). Cloisonné par
  // institution + utilisateur.
  const draftKey = React.useMemo(
    () => formDraftKey('institution', actualInstitutionId || institution?.id, user?.id),
    [actualInstitutionId, institution?.id, user?.id],
  );
  const draftRestoredRef = React.useRef(false);
  
  // Debug: afficher l'état du chargement (avec dépendances optimisées - limité en dev)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Debug FinancialInstitutionForm:', { 
        userId: user?.id,
        userFinancialInstitutionId: user?.financialInstitutionId, 
        actualInstitutionId,
        hasValidUUID,
        loading,
        isBackendHealthy,
        institutionId: institution?.id,
        institutionName: institution?.denominationSociale,
        initializedId: initializedInstitutionIdRef.current,
      });
    }
  }, [user?.id, actualInstitutionId, hasValidUUID, loading, institution?.id, isBackendHealthy]);
  
  // État pour la liste des institutions mères disponibles (pour les succursales)
  // Sécurité des soumissions : détection d'injections (SQL et prompt, car ces
  // profils nourrissent Adha), nettoyage des chaînes et limitation de débit.
  // Le jeton CSRF n'est pas activé : le backend s'appuie sur le JWT, en
  // produire un ici ne protégerait de rien.
  const {
    validateFormSecurity,
    sanitizeFormInput,
  } = useFormSecurity({
    enableCsrf: false,
    detectSqlInjection: true,
    detectPromptInjection: true,
    enableRateLimit: true,
    rateLimitAction: 'formSubmit',
    rateLimitIdentifier: user?.id || 'anonymous',
  });
  
  // État local pour le formulaire
  const [formData, setFormData] = useState({
    
    // Identification institutionnelle
    denominationSociale: '',
    sigle: '',
    typeInstitution: '',
    sousCategorie: '',
    dateCreation: '',
    paysOrigine: 'CD',
    statutJuridique: '',
    
    // Informations réglementaires
    autoritéSupervision: '',
    numeroAgrement: '',
    dateAgrement: '',
    validiteAgrement: '',
    numeroRCCM: '',
    numeroNIF: '',
    
    // Activités autorisées
    activitesAutorisees: [] as string[],
    
    // Informations opérationnelles
    siegeSocial: '',
    nombreAgences: 0,
    villesProvincesCouvertes: [] as string[],
    presenceInternationale: false,
    
    // Capacités financières
    capitalSocialMinimum: '',
    capitalSocialActuel: '',
    fondsPropresMontant: '',
    totalBilan: '',
    chiffreAffairesAnnuel: '',
    devise: 'USD' as 'USD' | 'CDF' | 'EUR',
    
    // Clientèle et marché
    segmentClientelePrincipal: '',
    nombreClientsActifs: 0,
    portefeuilleCredit: '',
    depotsCollectes: '',
    
    // Services offerts à Wanzo
    servicesCredit: [] as string[],
    servicesInvestissement: [] as string[],
    servicesGarantie: [] as string[],
    servicesTransactionnels: [] as string[],
    servicesConseil: [] as string[],
    
    // Partenariat Wanzo
    motivationPrincipale: '',
    servicesPrioritaires: [] as string[],
    segmentsClienteleCibles: [] as string[],
    volumeAffairesEnvisage: '',
    
    // Conditions commerciales
    grillesTarifaires: '',
    conditionsPreferentielles: '',
    delaisTraitement: '',
    criteresEligibilite: '',
    
    // Capacité d'engagement
    montantMaximumDossier: '',
    enveloppeGlobale: '',
    secteursActivitePrivilegies: [] as string[],
    zonesGeographiquesPrioritaires: [] as string[],
    
    // Documents
    documentsLegaux: [] as any[],
    documentsFinanciers: [] as any[],
    documentsOperationnels: [] as any[],
    documentsCompliance: [] as any[],
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  
  const { showToast } = useToast();

  // ✨ Fonction de validation des champs obligatoires
  const validateRequiredFields = useCallback((): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Champs obligatoires pour l'identification
    if (!formData.denominationSociale?.trim()) {
      errors.push('La dénomination sociale est obligatoire');
    }
    if (!formData.typeInstitution) {
      errors.push('Le type d\'institution est obligatoire');
    }
    if (!formData.dateCreation) {
      errors.push('La date de création est obligatoire');
    }
    if (!formData.paysOrigine) {
      errors.push('Le pays d\'origine est obligatoire');
    }
    
    // Champs obligatoires pour les informations réglementaires
    if (!formData.autoritéSupervision) {
      errors.push('L\'autorité de supervision est obligatoire');
    }
    if (!formData.numeroAgrement?.trim()) {
      errors.push('Le numéro d\'agrément est obligatoire');
    }
    if (!formData.dateAgrement) {
      errors.push('La date d\'agrément est obligatoire');
    }
    if (!formData.validiteAgrement) {
      errors.push('La date de validité de l\'agrément est obligatoire');
    }
    if (!formData.numeroRCCM?.trim()) {
      errors.push('Le numéro RCCM est obligatoire');
    }
    if (!formData.numeroNIF?.trim()) {
      errors.push('Le numéro NIF est obligatoire');
    }
    
    // Champ obligatoire pour le siège
    if (!formData.siegeSocial?.trim()) {
      errors.push('L\'adresse du siège social est obligatoire');
    }
    
    // Champs obligatoires pour le partenariat Wanzo
    if (!formData.segmentClientelePrincipal) {
      errors.push('Le segment de clientèle principal est obligatoire');
    }
    
    return { isValid: errors.length === 0, errors };
  }, [formData]);

  // La liste globale des institutions n'est plus chargée ici : elle servait
  // à choisir une institution mère, et exposait au passage tout le
  // portefeuille client. Le réseau d'agences vit dans sa propre section.

  // ✨ SIMPLIFICATION: useEffect pour charger les données
  useEffect(() => {
    if (!institution?.id || initializedInstitutionIdRef.current === institution.id) {
      return;
    }
    
    console.log('📥 Chargement des données dans le formulaire:', institution.denominationSociale);
    initializedInstitutionIdRef.current = institution.id;
    
    // Récupérer autoritéSupervision avec fallback
    const autoriteSup = (institution as any).autoritéSupervision || (institution as any).autoriteSupervision || '';
    
    setFormData(prev => ({
      ...prev,
      
      // Identification institutionnelle
      denominationSociale: institution.denominationSociale || '',
      sigle: institution.sigle || '',
      typeInstitution: institution.typeInstitution || '',
      sousCategorie: institution.sousCategorie || '',
      dateCreation: formatDateForInput(institution.dateCreation),
        paysOrigine: institution.paysOrigine || 'CD',
        statutJuridique: institution.statutJuridique || '',
        
        // Informations réglementaires
        // ✨ Utiliser le fallback pour autorité de supervision
        autoritéSupervision: autoriteSup,
        numeroAgrement: institution.numeroAgrement || '',
        // ✨ Convertir les dates ISO en format YYYY-MM-DD
        dateAgrement: formatDateForInput(institution.dateAgrement),
        validiteAgrement: formatDateForInput(institution.validiteAgrement),
        numeroRCCM: institution.numeroRCCM || '',
        numeroNIF: institution.numeroNIF || '',
        
        // Activités autorisées
        activitesAutorisees: institution.activitesAutorisees || [],
        
        // Informations opérationnelles
        siegeSocial: institution.siegeSocial || '',
        nombreAgences: institution.nombreAgences || 0,
        villesProvincesCouvertes: institution.villesProvincesCouvertes || [],
        presenceInternationale: institution.presenceInternationale || false,
        
        // Capacités financières
        capitalSocialMinimum: institution.capitalSocialMinimum || '',
        capitalSocialActuel: institution.capitalSocialActuel || '',
        fondsPropresMontant: institution.fondsPropresMontant || '',
        totalBilan: institution.totalBilan || '',
        chiffreAffairesAnnuel: institution.chiffreAffairesAnnuel || '',
        devise: institution.devise || 'USD',
        
        // Clientèle et marché
        segmentClientelePrincipal: institution.segmentClientelePrincipal || '',
        nombreClientsActifs: institution.nombreClientsActifs || 0,
        portefeuilleCredit: institution.portefeuilleCredit || '',
        depotsCollectes: institution.depotsCollectes || '',
        
        // Services offerts à Wanzo
        servicesCredit: institution.servicesCredit || [],
        servicesInvestissement: institution.servicesInvestissement || [],
        servicesGarantie: institution.servicesGarantie || [],
        servicesTransactionnels: institution.servicesTransactionnels || [],
        servicesConseil: institution.servicesConseil || [],
        
        // Partenariat Wanzo
        motivationPrincipale: institution.motivationPrincipale || '',
        servicesPrioritaires: institution.servicesPrioritaires || [],
        segmentsClienteleCibles: institution.segmentsClienteleCibles || [],
        volumeAffairesEnvisage: institution.volumeAffairesEnvisage || '',
        
        // Conditions commerciales
        grillesTarifaires: institution.grillesTarifaires || '',
        conditionsPreferentielles: institution.conditionsPreferentielles || '',
        delaisTraitement: institution.delaisTraitement || '',
        criteresEligibilite: institution.criteresEligibilite || '',
        
        // Capacité d'engagement
        montantMaximumDossier: institution.montantMaximumDossier || '',
        enveloppeGlobale: institution.enveloppeGlobale || '',
        secteursActivitePrivilegies: institution.secteursActivitePrivilegies || [],
        zonesGeographiquesPrioritaires: institution.zonesGeographiquesPrioritaires || [],
        
        // Documents
        documentsLegaux: institution.documentsLegaux || [],
        documentsFinanciers: institution.documentsFinanciers || [],
        documentsOperationnels: institution.documentsOperationnels || [],
        documentsCompliance: institution.documentsCompliance || [],
      }));
    console.log('✅ Données de l\'institution initialisées');
  }, [institution?.id]); // Dépendance uniquement sur l'ID

  // ── Restauration du brouillon (une seule fois), après l'init backend ────
  useEffect(() => {
    if (draftRestoredRef.current) return;
    // Attendre l'utilisateur, sinon la cle `…:anon` (vide) marquait la
    // restauration faite et le vrai brouillon etait ensuite ecrase.
    if (userLoading || !user?.id) return;
    if (actualInstitutionId && initializedInstitutionIdRef.current !== institution?.id) return;

    const draft = loadFormDraft<Partial<typeof formData>>(draftKey);
    draftRestoredRef.current = true;
    if (draft && Object.keys(draft).length > 0) {
      console.log('♻️ Brouillon du formulaire (institution) restauré');
      setFormData(prev => ({ ...prev, ...draft }));
      setIsEditing(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, institution?.id, actualInstitutionId, user?.id, userLoading]);

  // ── Sauvegarde automatique du brouillon (uniquement en mode édition) ────
  useEffect(() => {
    if (!isEditing) return;
    saveFormDraft(draftKey, formData);
  }, [formData, isEditing, draftKey]);

  // ✨ Calculer l'état d'initialisation
  const isFormInitialized = !actualInstitutionId || initializedInstitutionIdRef.current === institution?.id;

  const handleStartEditing = () => {
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    // Annulation explicite → on abandonne le brouillon local.
    clearFormDraft(draftKey);
    // Restaurer TOUTES les données originales depuis l'institution
    if (institution) {
      console.log('🔄 Restauration des données originales de l\'institution');
      
      // ✨ Récupérer autoritéSupervision avec fallback sur autoriteSupervision (sans accent du backend)
      const autoriteSup = (institution as any).autoritéSupervision || (institution as any).autoriteSupervision || '';
      
      setFormData(prev => ({
        ...prev,
        
        // Identification institutionnelle
        denominationSociale: institution.denominationSociale || '',
        sigle: institution.sigle || '',
        typeInstitution: institution.typeInstitution || '',
        sousCategorie: institution.sousCategorie || '',
        // ✨ Convertir les dates ISO en format YYYY-MM-DD
        dateCreation: formatDateForInput(institution.dateCreation),
        paysOrigine: institution.paysOrigine || 'CD',
        statutJuridique: institution.statutJuridique || '',
        
        // Informations réglementaires
        // ✨ Utiliser le fallback pour autorité de supervision
        autoritéSupervision: autoriteSup,
        numeroAgrement: institution.numeroAgrement || '',
        // ✨ Convertir les dates ISO en format YYYY-MM-DD
        dateAgrement: formatDateForInput(institution.dateAgrement),
        validiteAgrement: formatDateForInput(institution.validiteAgrement),
        numeroRCCM: institution.numeroRCCM || '',
        numeroNIF: institution.numeroNIF || '',
        
        // Activités autorisées
        activitesAutorisees: institution.activitesAutorisees || [],
        
        // Informations opérationnelles
        siegeSocial: institution.siegeSocial || '',
        nombreAgences: institution.nombreAgences || 0,
        villesProvincesCouvertes: institution.villesProvincesCouvertes || [],
        presenceInternationale: institution.presenceInternationale || false,
        
        // Capacités financières
        capitalSocialMinimum: institution.capitalSocialMinimum || '',
        capitalSocialActuel: institution.capitalSocialActuel || '',
        fondsPropresMontant: institution.fondsPropresMontant || '',
        totalBilan: institution.totalBilan || '',
        chiffreAffairesAnnuel: institution.chiffreAffairesAnnuel || '',
        devise: institution.devise || 'USD',
        
        // Clientèle et marché
        segmentClientelePrincipal: institution.segmentClientelePrincipal || '',
        nombreClientsActifs: institution.nombreClientsActifs || 0,
        portefeuilleCredit: institution.portefeuilleCredit || '',
        depotsCollectes: institution.depotsCollectes || '',
        
        // Services offerts à Wanzo
        servicesCredit: institution.servicesCredit || [],
        servicesInvestissement: institution.servicesInvestissement || [],
        servicesGarantie: institution.servicesGarantie || [],
        servicesTransactionnels: institution.servicesTransactionnels || [],
        servicesConseil: institution.servicesConseil || [],
        
        // Partenariat Wanzo
        motivationPrincipale: institution.motivationPrincipale || '',
        servicesPrioritaires: institution.servicesPrioritaires || [],
        segmentsClienteleCibles: institution.segmentsClienteleCibles || [],
        volumeAffairesEnvisage: institution.volumeAffairesEnvisage || '',
        
        // Conditions commerciales
        grillesTarifaires: institution.grillesTarifaires || '',
        conditionsPreferentielles: institution.conditionsPreferentielles || '',
        delaisTraitement: institution.delaisTraitement || '',
        criteresEligibilite: institution.criteresEligibilite || '',
        
        // Capacité d'engagement
        montantMaximumDossier: institution.montantMaximumDossier || '',
        enveloppeGlobale: institution.enveloppeGlobale || '',
        secteursActivitePrivilegies: institution.secteursActivitePrivilegies || [],
        zonesGeographiquesPrioritaires: institution.zonesGeographiquesPrioritaires || [],
        
        // Documents
        documentsLegaux: institution.documentsLegaux || [],
        documentsFinanciers: institution.documentsFinanciers || [],
        documentsOperationnels: institution.documentsOperationnels || [],
        documentsCompliance: institution.documentsCompliance || [],
      }));
    }
  };

  const handleSaveProfile = async () => {
    if (!isEditing) return;
    // Garde de sécurité avant tout envoi : une saisie piégée ne doit pas
    // atteindre le backend, ni l'assistant qui lira ce profil plus tard.
    const security = validateFormSecurity(formData);
    if (!security.isValid) {
      showToast({
        type: 'error',
        title: 'Saisie refusée',
        message: security.errors[0] || 'Votre saisie contient un contenu non autorisé.',
      });
      return;
    }
    const safeData = sanitizeFormInput(formData);
    
    // ✨ Validation des champs obligatoires
    const { isValid, errors } = validateRequiredFields();
    if (!isValid) {
      setValidationErrors(errors);
      showToast({
        type: 'error',
        title: 'Validation échouée',
        message: `Veuillez remplir tous les champs obligatoires (${errors.length} erreur${errors.length > 1 ? 's' : ''})`,
      });
      return;
    }
    setValidationErrors([]);
    
    setIsSaving(true);
    try {
      // ✨ NOUVEAU: Transmettre TOUTES les données du formulaire
      // Le formulaire est la source de vérité, on envoie tout directement
      // Le retour compte : `updateInstitutionData` rend `null` quand
      // l'enregistrement echoue. Enchainer sans le lire effacait le brouillon
      // alors que RIEN n'avait ete enregistre.
      const enregistree = await updateInstitutionData({
        
        // ============================================
        // SECTION: Identification Institutionnelle
        // ============================================
        denominationSociale: safeData.denominationSociale,
        sigle: safeData.sigle,
        typeInstitution: safeData.typeInstitution,
        sousCategorie: safeData.sousCategorie,
        dateCreation: safeData.dateCreation,
        paysOrigine: safeData.paysOrigine,
        statutJuridique: safeData.statutJuridique,
        
        // ============================================
        // SECTION: Informations Réglementaires
        // ============================================
        autoritéSupervision: safeData.autoritéSupervision,
        numeroAgrement: safeData.numeroAgrement,
        dateAgrement: safeData.dateAgrement,
        validiteAgrement: safeData.validiteAgrement,
        numeroRCCM: safeData.numeroRCCM,
        numeroNIF: safeData.numeroNIF,
        
        // ============================================
        // SECTION: Activités Autorisées
        // ============================================
        activitesAutorisees: safeData.activitesAutorisees,
        
        // ============================================
        // SECTION: Informations Opérationnelles
        // ============================================
        siegeSocial: safeData.siegeSocial,
        nombreAgences: safeData.nombreAgences,
        villesProvincesCouvertes: safeData.villesProvincesCouvertes,
        presenceInternationale: safeData.presenceInternationale,
        
        // ============================================
        // SECTION: Capacités Financières
        // ============================================
        capitalSocialMinimum: safeData.capitalSocialMinimum,
        capitalSocialActuel: safeData.capitalSocialActuel,
        fondsPropresMontant: safeData.fondsPropresMontant,
        totalBilan: safeData.totalBilan,
        chiffreAffairesAnnuel: safeData.chiffreAffairesAnnuel,
        devise: safeData.devise,
        
        // ============================================
        // SECTION: Clientèle et Marché
        // ============================================
        segmentClientelePrincipal: safeData.segmentClientelePrincipal,
        nombreClientsActifs: safeData.nombreClientsActifs,
        portefeuilleCredit: safeData.portefeuilleCredit,
        depotsCollectes: safeData.depotsCollectes,
        
        // ============================================
        // SECTION: Services Offerts à Wanzo
        // ============================================
        servicesCredit: safeData.servicesCredit,
        servicesInvestissement: safeData.servicesInvestissement,
        servicesGarantie: safeData.servicesGarantie,
        servicesTransactionnels: safeData.servicesTransactionnels,
        servicesConseil: safeData.servicesConseil,
        
        // ============================================
        // SECTION: Partenariat Wanzo
        // ============================================
        motivationPrincipale: safeData.motivationPrincipale,
        servicesPrioritaires: safeData.servicesPrioritaires,
        segmentsClienteleCibles: safeData.segmentsClienteleCibles,
        volumeAffairesEnvisage: safeData.volumeAffairesEnvisage,
        
        // ============================================
        // SECTION: Conditions Commerciales
        // ============================================
        grillesTarifaires: safeData.grillesTarifaires,
        conditionsPreferentielles: safeData.conditionsPreferentielles,
        delaisTraitement: safeData.delaisTraitement,
        criteresEligibilite: safeData.criteresEligibilite,
        
        // ============================================
        // SECTION: Capacité d'Engagement
        // ============================================
        montantMaximumDossier: safeData.montantMaximumDossier,
        enveloppeGlobale: safeData.enveloppeGlobale,
        secteursActivitePrivilegies: safeData.secteursActivitePrivilegies,
        zonesGeographiquesPrioritaires: safeData.zonesGeographiquesPrioritaires,
        
        // ============================================
        // SECTION: Documents Obligatoires
        // ============================================
        documentsLegaux: safeData.documentsLegaux,
        documentsFinanciers: safeData.documentsFinanciers,
        documentsOperationnels: safeData.documentsOperationnels,
        documentsCompliance: safeData.documentsCompliance,
      });
      if (!enregistree) {
        setIsSaving(false);
        return;
      }
      // Sauvegarde backend réussie → le brouillon local n'a plus lieu d'être.
      clearFormDraft(draftKey);
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = async (field: string, value: any) => {
    if (!isEditing) return;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getSousCategorieOptions = () => {
    if (formData.typeInstitution && formData.typeInstitution in FINANCIAL_INSTITUTION_SUBTYPES) {
      return FINANCIAL_INSTITUTION_SUBTYPES[formData.typeInstitution as keyof typeof FINANCIAL_INSTITUTION_SUBTYPES];
    }
    return [];
  };

  // ✨ SUPPRIMÉ: Le blocage du formulaire quand le backend n'est pas disponible
  // Le formulaire doit toujours être accessible, même hors ligne
  // Un avertissement non-bloquant sera affiché dans l'en-tête du formulaire

  // ✨ ANTI-VIBRATION: Afficher un loader stable pendant le chargement initial
  // Conditions claires: 
  // - user en chargement 
  // - institutionId existe mais pas encore chargé
  // Note: On ne bloque plus pour la vérification backend
  const isInitialLoading = userLoading || (actualInstitutionId && !institution && loading);
  
  // ✨ ANTI-VIBRATION: Attendre que les données soient prêtes avant d'afficher le formulaire
  const waitingForData = actualInstitutionId && !isFormInitialized;
  
  if (isInitialLoading || waitingForData) {
    return (
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#197ca8]" />
          <p className="mt-4 text-gray-600">
            Chargement du formulaire...
          </p>
        </div>
      </div>
    );
  }

  // ✨ Déterminer si c'est une nouvelle institution (pas d'ID valide)
  const isNewInstitution = !hasValidUUID;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      {/* En-tête */}
      <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0 mb-6 pb-4 border-b border-gray-200">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
            <Building2 className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-[#197ca8]" />
            <span className="break-words">Identification Institution Financière</span>
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Formulaire de partenariat pour institutions financières - Informations simplifiées
          </p>
          {/* ✨ Indicateur de connexion backend - adaptatif selon l'état */}
          {isCheckingBackend ? (
            <div className="flex items-center text-xs text-gray-500 mt-1">
              <Loader2 className="w-3 h-3 animate-spin mr-2" />
              Vérification de la connexion...
            </div>
          ) : backendStatus === 'healthy' ? (
            <div className="flex items-center text-xs text-green-600 mt-1">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse" />
              Connecté au serveur
            </div>
          ) : (
            <div className="flex items-center text-xs text-amber-600 mt-1 cursor-pointer" onClick={() => checkHealth()}>
              <WifiOff className="w-3 h-3 mr-2" />
              Mode hors ligne - <span className="underline ml-1">Réessayer</span>
            </div>
          )}
          {loading && isFormInitialized && (
            <div className="flex items-center text-sm text-[#197ca8] mt-1">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Synchronisation...
            </div>
          )}
          {isNewInstitution && (
            <div className="flex items-center text-sm text-amber-600 mt-1">
              <Building2 className="w-4 h-4 mr-2" />
              Nouvelle institution - Remplissez le formulaire et sauvegardez
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
          {!isEditing ? (
            <button
              onClick={handleStartEditing}
              className="inline-flex items-center px-3 py-2 sm:px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#197ca8] hover:bg-[#156a92] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] transition-colors"
            >
              <Edit3 className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{isNewInstitution ? 'Créer le profil' : 'Éditer le profil'}</span>
              <span className="sm:hidden">{isNewInstitution ? 'Créer' : 'Éditer'}</span>
            </button>
          ) : (
            <>
              <button
                onClick={handleCancelEditing}
                disabled={isSaving}
                className="inline-flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] transition-colors disabled:opacity-50"
              >
                <X className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Annuler</span>
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={isSaving}
                className="inline-flex items-center px-3 py-2 sm:px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1 sm:mr-2" />
                )}
                {isSaving ? 'Sauvegarde...' : (isNewInstitution ? 'Créer' : 'Sauvegarder')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Affichage des erreurs de validation */}
      {validationErrors.length > 0 && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-800">
                Veuillez corriger les erreurs suivantes :
              </h3>
              <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="identification">
        <TabsList className="mb-8">
          <TabsTrigger value="identification">
            <Building2 className="w-4 h-4 mr-2" />
            Identification
          </TabsTrigger>
          <TabsTrigger value="reglementaire">
            <Shield className="w-4 h-4 mr-2" />
            Réglementaire
          </TabsTrigger>
          <TabsTrigger value="partenariat">
            <Handshake className="w-4 h-4 mr-2" />
            Partenariat
          </TabsTrigger>
          <TabsTrigger value="documents">
            <FileText className="w-4 h-4 mr-2" />
            Documents
          </TabsTrigger>
        </TabsList>

        {/* Section Identification */}
        <TabsContent value="identification">
          <div className="space-y-8">
            {/* Le réseau d'agences remplace l'ancien rattachement à une
                institution mère : une institution déclare où elle opère, elle
                ne se rattache pas à une autre depuis un annuaire global. */}
            <InstitutionBranchesSection
              institutionId={institution?.id || financialInstitutionId}
              isEditing={isEditing}
            />

            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Informations de Base
              </h2>
              
              {/* Logo de l'institution */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Logo de l'institution financière
                </label>
                {/* ✨ Afficher EntityLogo seulement si l'institution a un ID valide */}
                {institution?.id ? (
                  <EntityLogo
                    entityId={institution.id}
                    logo={institution?.logo}
                    name={formData.denominationSociale || 'Votre Institution'}
                    onUpload={uploadLogoFile}
                    isUploading={isLogoUploading}
                    onLogoUpdate={(success, newUrl) => {
                      if (success && newUrl) {
                        console.log('✅ Logo de l\'institution mis à jour:', newUrl);
                      }
                    }}
                  />
                ) : (
                  /* Placeholder quand pas d'ID (nouvelle institution) */
                  <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden border-2 border-gray-200 shadow-sm flex items-center justify-center bg-primary-100">
                    <span className="text-primary-700 text-2xl font-bold">
                      {(formData.denominationSociale || 'IF').split(' ').filter(w => w.length > 0).map(w => w[0]).join('').substring(0, 2).toUpperCase() || 'IF'}
                    </span>
                  </div>
                )}
                {!institution?.id && (
                  <p className="text-xs text-gray-500 mt-2">Le logo pourra être ajouté après la création de l'institution.</p>
                )}
              </div>
              
              <div className="space-y-6">
                <EditableField
                  label="Dénomination sociale complète *"
                  value={formData.denominationSociale}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, denominationSociale: value.toString() }));
                  }}
                  disabled={!isEditing}
                  required
                />

                <EditableField
                  label="Sigle ou nom commercial"
                  value={formData.sigle}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, sigle: value.toString() }));
                  }}
                  disabled={!isEditing}
                />

                <EditableField
                  label="Type d'institution *"
                  value={formData.typeInstitution}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, typeInstitution: value.toString(), sousCategorie: '' }));
                  }}
                  type="select"
                  options={FINANCIAL_INSTITUTION_TYPES as any}
                  disabled={!isEditing}
                  required
                />

                {formData.typeInstitution && (
                  <EditableField
                    label="Sous-catégorie spécifique *"
                    value={formData.sousCategorie}
                    onSave={async (value) => {
                      if (!isEditing) return;
                      setFormData(prev => ({ ...prev, sousCategorie: value.toString() }));
                    }}
                    type="select"
                    options={getSousCategorieOptions() as any}
                    disabled={!isEditing}
                    required
                  />
                )}

                <EditableField
                  label="Date de création/agrément *"
                  value={formData.dateCreation}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, dateCreation: value.toString() }));
                  }}
                  type="date"
                  disabled={!isEditing}
                  required
                />

                <EditableField
                  label="Statut juridique"
                  value={formData.statutJuridique}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, statutJuridique: value.toString() }));
                  }}
                  type="select"
                  options={LEGAL_STATUSES as any}
                  disabled={!isEditing}
                />

                <EditableField
                  label="Pays d'origine *"
                  value={formData.paysOrigine}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, paysOrigine: value.toString() }));
                  }}
                  type="select"
                  options={getAfricanCountryOptions(true) as any}
                  disabled={!isEditing}
                  required
                />
              </div>
            </section>

            {/* Informations opérationnelles */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Informations Opérationnelles
              </h2>
              <div className="space-y-6">
                <EditableField
                  label="Adresse du siège social *"
                  value={formData.siegeSocial}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, siegeSocial: value.toString() }));
                  }}
                  type="textarea"
                  disabled={!isEditing}
                  required
                />

                <EditableField
                  label="Nombre d'agences/succursales"
                  value={formData.nombreAgences.toString()}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, nombreAgences: parseInt(value.toString()) || 0 }));
                  }}
                  type="number"
                  disabled={!isEditing}
                />

                <TagInput
                  label="Villes/Provinces couvertes"
                  value={formData.villesProvincesCouvertes}
                  onSave={(tags) => handleFieldChange('villesProvincesCouvertes', tags)}
                  disabled={!isEditing}
                  placeholder="Ex: Kinshasa, Goma, Lubumbashi..."
                />

                <EditableField
                  label="Segment de clientèle principal *"
                  value={formData.segmentClientelePrincipal}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, segmentClientelePrincipal: value.toString() }));
                  }}
                  type="select"
                  options={CLIENT_SEGMENTS as any}
                  disabled={!isEditing}
                  required
                />

                <EditableField
                  label="Nombre de clients actifs"
                  value={formData.nombreClientsActifs.toString()}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, nombreClientsActifs: parseInt(value.toString()) || 0 }));
                  }}
                  type="number"
                  disabled={!isEditing}
                />
              </div>
            </section>
          </div>
        </TabsContent>

        {/* Section Réglementaire */}
        <TabsContent value="reglementaire">
          <div className="space-y-8">
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Informations Réglementaires
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Les champs marqués d'un astérisque (*) sont obligatoires.
              </p>
              <div className="space-y-6">
                <EditableField
                  label="Autorité de supervision *"
                  value={formData.autoritéSupervision}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, autoritéSupervision: value.toString() }));
                  }}
                  type="select"
                  options={SUPERVISORY_AUTHORITIES as any}
                  disabled={!isEditing}
                  required
                />

                <EditableField
                  label="Numéro d'agrément/licence *"
                  value={formData.numeroAgrement}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, numeroAgrement: value.toString() }));
                  }}
                  disabled={!isEditing}
                  required
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <EditableField
                    label="Date d'obtention de l'agrément *"
                    value={formData.dateAgrement}
                    onSave={async (value) => {
                      if (!isEditing) return;
                      setFormData(prev => ({ ...prev, dateAgrement: value.toString() }));
                    }}
                    type="date"
                    disabled={!isEditing}
                    required
                  />

                  <EditableField
                    label="Date de validité de l'agrément *"
                    value={formData.validiteAgrement}
                    onSave={async (value) => {
                      if (!isEditing) return;
                      setFormData(prev => ({ ...prev, validiteAgrement: value.toString() }));
                    }}
                    type="date"
                    disabled={!isEditing}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <EditableField
                    label="Numéro RCCM *"
                    value={formData.numeroRCCM}
                    onSave={async (value) => {
                      if (!isEditing) return;
                      setFormData(prev => ({ ...prev, numeroRCCM: value.toString() }));
                    }}
                    disabled={!isEditing}
                    required
                  />

                  <EditableField
                    label="Numéro d'identification fiscale (NIF) *"
                    value={formData.numeroNIF}
                    onSave={async (value) => {
                      if (!isEditing) return;
                      setFormData(prev => ({ ...prev, numeroNIF: value.toString() }));
                    }}
                    disabled={!isEditing}
                    required
                  />
                </div>
              </div>
            </section>

            {/* Activités autorisées */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Activités Autorisées
              </h2>
              <TagInput
                label="Services autorisés par votre agrément"
                value={formData.activitesAutorisees}
                onSave={(tags) => handleFieldChange('activitesAutorisees', tags)}
                disabled={!isEditing}
                placeholder="Sélectionnez vos activités autorisées..."
              />
            </section>

            {/* Capacités financières */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Capacités Financières
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <EditableField
                  label="Capital social actuel"
                  value={formData.capitalSocialActuel}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, capitalSocialActuel: value.toString() }));
                  }}
                  type="number"
                  disabled={!isEditing}
                />

                <EditableField
                  label="Devise de référence"
                  value={formData.devise}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, devise: value as 'USD' | 'CDF' | 'EUR' }));
                  }}
                  type="select"
                  options={CURRENCIES as any}
                  disabled={!isEditing}
                />

                <EditableField
                  label="Fonds propres"
                  value={formData.fondsPropresMontant}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, fondsPropresMontant: value.toString() }));
                  }}
                  type="number"
                  disabled={!isEditing}
                />

                <EditableField
                  label="Chiffre d'affaires annuel"
                  value={formData.chiffreAffairesAnnuel}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, chiffreAffairesAnnuel: value.toString() }));
                  }}
                  type="number"
                  disabled={!isEditing}
                />
              </div>
            </section>
          </div>
        </TabsContent>

        {/* Section Partenariat */}
        <TabsContent value="partenariat">
          <div className="space-y-8">
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Services offerts à l'écosystème Wanzo
              </h2>
              <div className="space-y-6">
                <TagInput
                  label="Services de crédit"
                  value={formData.servicesCredit}
                  onSave={(tags) => handleFieldChange('servicesCredit', tags)}
                  disabled={!isEditing}
                />

                <TagInput
                  label="Services d'investissement"
                  value={formData.servicesInvestissement}
                  onSave={(tags) => handleFieldChange('servicesInvestissement', tags)}
                  disabled={!isEditing}
                />

                <TagInput
                  label="Services de garantie"
                  value={formData.servicesGarantie}
                  onSave={(tags) => handleFieldChange('servicesGarantie', tags)}
                  disabled={!isEditing}
                />

                <TagInput
                  label="Services transactionnels"
                  value={formData.servicesTransactionnels}
                  onSave={(tags) => handleFieldChange('servicesTransactionnels', tags)}
                  disabled={!isEditing}
                />

                <TagInput
                  label="Services de conseil"
                  value={formData.servicesConseil}
                  onSave={(tags) => handleFieldChange('servicesConseil', tags)}
                  disabled={!isEditing}
                />
              </div>
            </section>

            {/* Les conditions commerciales (enveloppes, grilles tarifaires,
                conditions préférentielles) ne sont plus demandées ici : elles
                se négocient avec l'équipe Wanzo, pas dans un formulaire
                d'inscription. Les champs restent en base pour les dossiers qui
                les portent déjà. */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Conditions commerciales
              </h2>
              <p className="text-sm text-gray-600">
                Enveloppes, plafonds par dossier et conditions tarifaires se
                fixent avec notre équipe une fois votre dossier validé. Rien à
                remplir ici.
              </p>
            </section>
          </div>
        </TabsContent>

        {/* Section Documents */}
        <TabsContent value="documents">
          <div className="space-y-8">
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Documents Obligatoires
              </h2>
              
              <div className="space-y-8">
                <FileUpload
                  label="Documents Légaux et Réglementaires"
                  description="Statuts, agrément, RCCM, NIF, attestations de conformité"
                  value={formData.documentsLegaux}
                  onSave={(files) => handleFieldChange('documentsLegaux', files)}
                  disabled={!isEditing}
                  multiple={true}
                  acceptedTypes={['.pdf', '.doc', '.docx', '.jpg', '.png']}
                  maxSize={10}
                />

                <FileUpload
                  label="Documents Financiers"
                  description="États financiers audités (3 derniers exercices), rapport annuel, notation"
                  value={formData.documentsFinanciers}
                  onSave={(files) => handleFieldChange('documentsFinanciers', files)}
                  disabled={!isEditing}
                  multiple={true}
                  acceptedTypes={['.pdf', '.doc', '.docx', '.xls', '.xlsx']}
                  maxSize={20}
                />

                <FileUpload
                  label="Documents Opérationnels"
                  description="Organigramme, CV dirigeants, présentation institutionnelle, grille tarifaire"
                  value={formData.documentsOperationnels}
                  onSave={(files) => handleFieldChange('documentsOperationnels', files)}
                  disabled={!isEditing}
                  multiple={true}
                  acceptedTypes={['.pdf', '.doc', '.docx', '.ppt', '.pptx']}
                  maxSize={15}
                />

                <FileUpload
                  label="Documents de Compliance"
                  description="Politique KYC/AML, procédures de contrôle interne, certifications"
                  value={formData.documentsCompliance}
                  onSave={(files) => handleFieldChange('documentsCompliance', files)}
                  disabled={!isEditing}
                  multiple={true}
                  acceptedTypes={['.pdf', '.doc', '.docx']}
                  maxSize={10}
                />
              </div>
            </section>
          </div>
        </TabsContent>
      </Tabs>

      {/* Section d'extraction PDF */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-4 sm:mb-0">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Extraction du formulaire
              </h3>
              <p className="text-sm text-gray-600">
                Téléchargez votre formulaire d'identification au format PDF pour archivage ou soumission externe.
              </p>
            </div>
            <div className="flex-shrink-0">
              <ProfessionalPDFExtractionButton
                type="financial-institution"
                formData={formData}
                entityName={formData.denominationSociale || institution?.denominationSociale || "Mon institution"}
                disabled={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialInstitutionForm;